import {
  IDENTITY_UNIQUENESS_POLICY,
  isIdentityUniquenessPolicyReady,
} from "./identity-uniqueness-policy.js";
import {
  ACCOUNT_ELIGIBILITY_POLICY,
  isAccountEligibilityPolicyReady,
} from "./account-eligibility-policy.js";

export const REGISTRATION_ACTIVATION_GATES = Object.freeze([
  Object.freeze({ key: "founderUnlockApproved", label: "Explicit founder approval to unlock registration" }),
  Object.freeze({ key: "operatorIdentityVerified", label: "Verified legal operator name and valid mailing or service address" }),
  Object.freeze({ key: "legalReviewApproved", label: "Qualified legal review of the launch Terms, Privacy Notice, and consent flow" }),
  Object.freeze({ key: "authEnabled", label: "Server-side authentication and session controls" }),
  Object.freeze({ key: "enabled", label: "Registration feature enabled" }),
  Object.freeze({ key: "mutationsEnabled", label: "Server-side account mutations enabled" }),
  Object.freeze({ key: "emailVerificationEnabled", label: "Time-limited server-side email verification" }),
  Object.freeze({ key: "emailDispatchEnabled", label: "Authenticated email dispatch with abuse controls" }),
  Object.freeze({ key: "identityUniquenessEnforced", label: "Database-enforced username, email, and avatar-key uniqueness" }),
  Object.freeze({ key: "pendingIdentityReservationsEnabled", label: "Atomic expiring reservations for pending identity verification" }),
  Object.freeze({ key: "avatarOwnershipVerificationEnabled", label: "Verified Second Life avatar-key ownership challenge" }),
  Object.freeze({ key: "reservedBrandIdentityPolicyEnforced", label: "Server-enforced reserved Exile and Exiled identity policy" }),
  Object.freeze({ key: "supportedAgeRangePolicyEnforced", label: "Server-enforced supported 18-through-98 age range" }),
  Object.freeze({ key: "approvedEmailProviderPolicyEnforced", label: "Server-enforced exact approved email-provider policy" }),
  Object.freeze({ key: "consentPersistenceEnabled", label: "Immutable server-side consent receipt persistence" }),
  Object.freeze({ key: "serverPolicyReceiptEnabled", label: "Server receipt of the exact policy ID, version, revision, and content hash" }),
  Object.freeze({ key: "submissionHandlerConnected", label: "Validated server-side signup submission handler" }),
  Object.freeze({ key: "termsEffective", label: "Founder-approved effective Terms of Service" }),
  Object.freeze({ key: "privacyNoticeEffective", label: "Effective Privacy Notice" }),
  Object.freeze({ key: "privacyDataMapApproved", label: "Approved processor, cookie, analytics, retention, and user-rights data map" }),
  Object.freeze({ key: "ageAssuranceEnabled", label: "Approved minimum-data 18+ age-assurance workflow" }),
  Object.freeze({ key: "contactWorkflowEnabled", label: "Monitored staff contact, appeal, safety, and privacy workflow" }),
  Object.freeze({ key: "moderationRbacEnabled", label: "Server-enforced moderation roles and least-privilege account access" }),
  Object.freeze({ key: "adminAuditLoggingEnabled", label: "Administrator action logging and protected retention" }),
  Object.freeze({ key: "accountClosureExportEnabled", label: "Self-service account closure and pre-deletion media export" }),
  Object.freeze({ key: "chatDeletionVerified", label: "Verified rolling 30-day ordinary-chat deletion" }),
  Object.freeze({ key: "backupDeletionVerified", label: "Verified 24-hour chat-backup expiry with lawful hold exceptions" }),
  Object.freeze({ key: "childSafetyWorkflowEnabled", label: "Trained child-safety reporting and preservation workflow" }),
  Object.freeze({ key: "intimateImageRemovalWorkflowEnabled", label: "Non-user-accessible intimate-image notice and 48-hour removal workflow" }),
  Object.freeze({ key: "copyrightWorkflowEnabled", label: "Attorney-approved copyright workflow and DMCA readiness appropriate to launch scope" }),
  Object.freeze({ key: "supportTermsApproved", label: "Approved support-product delivery, refund, tax, and solicitation disclosures" }),
  Object.freeze({ key: "securityControlsEnabled", label: "Server-side validation, rate limiting, authorization, and security controls" }),
]);

export const REGISTRATION_STATE = Object.freeze({
  founderUnlockApproved: false,
  operatorIdentityVerified: false,
  legalReviewApproved: false,
  authEnabled: false,
  enabled: false,
  mutationsEnabled: false,
  emailVerificationEnabled: false,
  emailDispatchEnabled: false,
  identityUniquenessEnforced: false,
  pendingIdentityReservationsEnabled: false,
  avatarOwnershipVerificationEnabled: false,
  reservedBrandIdentityPolicyEnforced: false,
  supportedAgeRangePolicyEnforced: false,
  approvedEmailProviderPolicyEnforced: false,
  consentPersistenceEnabled: false,
  serverPolicyReceiptEnabled: false,
  submissionHandlerConnected: false,
  termsEffective: true,
  privacyNoticeEffective: false,
  privacyDataMapApproved: false,
  ageAssuranceEnabled: false,
  contactWorkflowEnabled: false,
  moderationRbacEnabled: false,
  adminAuditLoggingEnabled: false,
  accountClosureExportEnabled: false,
  chatDeletionVerified: false,
  backupDeletionVerified: false,
  childSafetyWorkflowEnabled: false,
  intimateImageRemovalWorkflowEnabled: false,
  copyrightWorkflowEnabled: false,
  supportTermsApproved: false,
  securityControlsEnabled: false,
  status: "locked",
  statusLabel: "REGISTRATION LOCKED",
  signupRoute: "/signup/",
  termsRoute: "/terms/",
  contactRoute: "/contact/",
  loginRoute: "/login/",
  publicMessage: "Account creation remains locked until every founder, legal, privacy, age, safety, deletion, contact, email, identity-uniqueness, consent, and server-security gate is verified.",
  activationRequires: Object.freeze(REGISTRATION_ACTIVATION_GATES.map(({ label }) => label)),
});

export function isRegistrationActivationReady(
  state = REGISTRATION_STATE,
  identityPolicy = IDENTITY_UNIQUENESS_POLICY,
  accountEligibilityPolicy = ACCOUNT_ELIGIBILITY_POLICY,
) {
  return Boolean(
    state?.status === "open"
    && isIdentityUniquenessPolicyReady(state, identityPolicy)
    && isAccountEligibilityPolicyReady(state, accountEligibilityPolicy)
    && REGISTRATION_ACTIVATION_GATES.every(({ key }) => state?.[key] === true)
  );
}

